import { Table } from '@chakra-ui/react';
import { SubscriptionViewOffering } from '@monetize/types/app';
import { CurrencyCodes } from '@monetize/types/common';
import { toDateShort } from '@monetize/utils/core';
import { Fragment, useMemo, useState } from 'react';
import { MBox, MButton, MFlex, MText } from '../Monetize';
import { MTag } from '../Monetize/chakra';
import { SubscriptionItemRows } from './SubscriptionItemRows';
import { TableCell } from './SubscriptionViewTable';

interface SubscriptionOfferingSectionProps {
  offering: SubscriptionViewOffering;
  currency: CurrencyCodes;
}

export const SubscriptionOfferingSection = ({
  offering,
  currency,
}: SubscriptionOfferingSectionProps) => {
  const [showRamps, setShowRamps] = useState(false);
  const { activeSegment, hasRamps, allSegments, isRemoved } = offering;
  const offeringName =
    activeSegment.offeringName ?? activeSegment.rateName ?? 'Offering';

  // Active span for the offering across all of its (ramp) segments, plus a
  // "Removed as of" note when the offering has been removed. The backend only
  // returns removed offerings that were active for part of the term, so the
  // offering end date is the date it was removed.
  const { activeDateLabel, removedLabel } = useMemo(() => {
    const startDate = allSegments.reduce(
      (min, segment) => (segment.startDate < min ? segment.startDate : min),
      activeSegment.startDate,
    );
    const endDate = allSegments.reduce(
      (max, segment) => (segment.endDate > max ? segment.endDate : max),
      activeSegment.endDate,
    );

    return {
      activeDateLabel:
        startDate === endDate
          ? toDateShort(startDate)
          : `${toDateShort(startDate)} – ${toDateShort(endDate)}`,
      removedLabel: isRemoved ? `Removed as of ${toDateShort(endDate)}` : null,
    };
  }, [allSegments, activeSegment, isRemoved]);

  return (
    <Table.Body>
      <Table.Row>
        <TableCell colSpan={3} bg="bg.subtle" py={2} px={4}>
          <MFlex
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <MBox>
              <MText fontWeight="700" color="tPurple.dark">
                {offeringName}
              </MText>
              {activeDateLabel && (
                <MText fontSize="xs" color="fg.muted">
                  {activeDateLabel}
                </MText>
              )}
              {removedLabel && (
                <MText fontSize="xs" color="fg.muted">
                  {removedLabel}
                </MText>
              )}
            </MBox>
          </MFlex>
        </TableCell>
      </Table.Row>

      {hasRamps && (
        <Table.Row>
          <TableCell colSpan={3} px={4} py={2}>
            <MButton
              type="button"
              variant="tertiary"
              size="sm"
              onClick={() => setShowRamps((prev) => !prev)}
            >
              {showRamps ? 'Hide Ramps' : `Show Ramps (${allSegments.length})`}
            </MButton>
          </TableCell>
        </Table.Row>
      )}

      {!showRamps && (
        <SubscriptionItemRows
          items={activeSegment.items}
          currency={currency}
          billingFrequency={activeSegment.billingFrequency}
          billingFrequencyInMonths={activeSegment.billingFrequencyInMonths}
        />
      )}

      {hasRamps &&
        showRamps &&
        allSegments.map((segment) => {
          const isCurrent =
            segment.quoteOfferingId === activeSegment.quoteOfferingId;
          const accentColor = isCurrent ? 'tIndigo.base' : 'border.subtle';
          return (
            <Fragment key={segment.quoteOfferingId}>
              <Table.Row borderLeft="3px solid" borderLeftColor={accentColor}>
                <TableCell colSpan={3} px={4} py={2} bg="gray.50">
                  <MFlex alignItems="center" gap={2} flexWrap="wrap" pl={2}>
                    <MText fontSize="sm" fontWeight="600" color="tPurple.dark">
                      {toDateShort(segment.startDate)} –{' '}
                      {toDateShort(segment.endDate)}
                    </MText>
                    {isCurrent && (
                      <MTag variant="blue" size="sm">
                        Current
                      </MTag>
                    )}
                  </MFlex>
                </TableCell>
              </Table.Row>
              <SubscriptionItemRows
                items={segment.items}
                currency={currency}
                billingFrequency={segment.billingFrequency}
                billingFrequencyInMonths={segment.billingFrequencyInMonths}
                isRamp
                isCurrentRamp={isCurrent}
              />
            </Fragment>
          );
        })}
    </Table.Body>
  );
};
